import React, { useEffect } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import Grid from "@mui/material/Grid2";
import { Box, Button, Typography } from "@mui/material";
import { formatDate } from '../../utils/common';
import { useStoreState } from 'easy-peasy';
import { stringConstants } from '../../constants/stringConstants';
import PACheckResultsProcedure from './PACheckResultsProcedure';
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorIcon from '@mui/icons-material/Error';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import AssignmentIcon from '@mui/icons-material/Assignment';

const PACheckResults = () => {
	const { orgId } = useParams();
	const navigate = useNavigate();
	const handlePrint = () => window.print();
	const brandingData = useStoreState((state) => state?.paCheck?.brandingData);
	const postData = useStoreState((state) => state?.paCheck?.postData);
	const paCheckResults = useStoreState(
		(state) => state?.paCheck?.pACheckResults
	) || [];

	const [
		transactionData,
		groupData,
		globalData,
		proceduresData,
	] = paCheckResults;

	const getGlobalTabDataOBJ = (arr, val) => arr?.find(o => o.type === val);

	const createMarkup = (data) => {
    let markup = (data ? data : '');
    return { __html: markup };
  }

	const proceduresList = (data, key) => {
		return data?.filter(item => item.pa_required === key)
	};

	const getEditorData = (usertype, editorData) => {
		return usertype === "provider" ? editorData?.provider_callout : editorData?.member_callout;
	}

	const instructionList = (data, key) => {
		return data?.filter(item => item.type === key)?.[0]?.instructions || [];
	};

	useEffect(() => {
		if(paCheckResults.length === 0){
			navigate(`/${orgId}/${stringConstants.PA_CHECK_FORM_URL}`);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paCheckResults]);

	const getSummaryBoxHTML = (color, icon, count, text) => {
		return (
			<Box
				sx={{
					mt: 1,
					display: "flex",
					alignItems: "center",
				}}
			>
				{icon === 'red' ? (
					<ErrorIcon sx={{ color: {color}, mr: 1 }} />
				) : icon === 'green' ? (
					<CheckCircleRoundedIcon sx={{ color: '#057251', mr: 1 }} />
				) : (
					<HelpRoundedIcon sx={{ color: '#000', mr: 1 }} />
				)}				
				<span style={{fontWeight: "bold"}}>{count} Procedures</span>
				&nbsp;{text}
				<span style={{fontWeight: "bold"}}>&nbsp;— See below for next steps.</span>
			</Box>
		)
	}

  return (
    <Box component="section" className="headerBox">
			<Grid container className="container">
				<Grid container spacing={2}>
					<Grid item size={12}>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							<Typography sx={{ fontSize: 14, color: '#555' }}>
								<b>Transaction ID:</b> {transactionData?.data?.[0]?.trans_id || 'N/A'}
							</Typography>
							<Button className="noPrint" color="primary" variant="contained" onClick={handlePrint} sx={{ padding: '6px 12px', borderRadius: '4px', cursor: 'pointer' }}>
								Print
							</Button>
						</Box>
					</Grid>
					
					<Grid item size={12}>
						{/* Top Info Box */}
						<Box sx={{ background: "#eaf4fa", p: 2, mb: 2, borderRadius: 1 }}>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6}>
									<Typography><b>Member ID Group Number:</b> {getGlobalTabDataOBJ(globalData?.tab_data, "group")?.record_number || 'N/A'}</Typography>
									<Typography><b>Date of Service:</b> {formatDate(groupData?.data?.[0]["service_date"])}</Typography>
									<Typography>
										<b>Line of Business:</b> {getGlobalTabDataOBJ(globalData?.tab_data, 'lob')?.title || 'N/A'}
									</Typography>
								</Grid>
							</Grid>
						</Box>
						{/* Instructions Box */}
						<Box sx={{backgroundColor: "lightblue", color: "rgba(0, 0, 0, 0.87)", p: 1, borderRadius: 1, mb: 1}}>
							<AssignmentIcon sx={{ color: "rgba(0, 0, 0, 0.87)", mr: 1 }} />				
							<span style={{position: 'relative', top: '-5px'}}><strong>Instructions</strong></span>
						</Box>
						<Box sx={{ pl: 2, pr: 2, mb: 2, }}>
							{
								instructionList(globalData?.tab_data, 'lob').length > 0 || 
								instructionList(globalData?.tab_data, 'client').length > 0 || 
								instructionList(globalData?.tab_data, 'program').length > 0 || 
								groupData?.data?.[0]?.instructions?.length > 0 ? (
								<>
									{groupData?.data?.[0]?.instructions.map((instruction, idx) => (
										<Typography key={idx} sx={{ mb: 1 }}>{instruction.text}</Typography>
									))}
									{instructionList(globalData?.tab_data, 'lob').map((instruction, idx) => (
										<Typography key={idx} sx={{ mb: 1 }}>{instruction.text}</Typography>
									))}
									{instructionList(globalData?.tab_data, 'client').map((instruction, idx) => (
										<Typography key={idx} sx={{ mb: 1 }}>{instruction.text}</Typography>
									))}
									{instructionList(globalData?.tab_data, 'program').map((instruction, idx) => (
										<Typography key={idx} sx={{ mb: 1 }}>{instruction.text}</Typography>
									))}
								</>
							) : (
								<Typography sx={{ mb: 1 }}>No instructions available.</Typography>
							)}
						</Box>
						{/* Summary Box */}
						<Box sx={{ background: "#f7f7f7", p: 2, mb: 2, borderRadius: 1 }}>
							<Typography variant="subtitle1" fontWeight="bold">
								Summary:
							</Typography>

							<Typography sx={{ mt: 1 }}>
								You submitted {proceduresData?.data?.length} procedures:
							</Typography>
							{proceduresList(proceduresData?.data, 'Y')?.length > 0 && (
								getSummaryBoxHTML('#ff0000', 'red', proceduresList(proceduresData?.data, 'Y')?.length, brandingData?.config_rules?.labels?.y?.toUpperCase())								
							)}
							{proceduresList(proceduresData?.data, 'N')?.length > 0 && (
								getSummaryBoxHTML('#057251', 'green', proceduresList(proceduresData?.data, 'N')?.length, brandingData?.config_rules?.labels?.n?.toUpperCase())
							)}
							{proceduresList(proceduresData?.data, 'U')?.length > 0 && (
								getSummaryBoxHTML('#057251', 'black', proceduresList(proceduresData?.data, 'U')?.length, brandingData?.config_rules?.labels?.u?.toUpperCase())
							)}
						</Box>
						{/* Red Part */}
						{proceduresList(proceduresData?.data, 'Y')?.length > 0 && (
							<React.Fragment>
								<PACheckResultsProcedure 
									color="#ff0000" 
									submitProcCount={proceduresData?.data?.length}
									proceduresList={proceduresList(proceduresData?.data, 'Y')}
									title={brandingData.config_rules?.labels?.y?.toUpperCase()}
								/>
								{getEditorData(postData?.user_type, brandingData?.config_text)?.pa_required && (
									<Box
										sx={{
											background: "#ffeaeb",
										}}
										className="next-steps-box"
									>
										<div dangerouslySetInnerHTML={createMarkup(getEditorData(postData?.user_type, brandingData?.config_text)?.pa_required)}></div>
									</Box>
								)}
							</React.Fragment>
						)}
						{/* Green Part */}
						{proceduresList(proceduresData?.data, 'N')?.length > 0 && (
							<React.Fragment>
								<PACheckResultsProcedure 
									color="#057251" 
									submitProcCount={proceduresData?.data?.length}
									proceduresList={proceduresList(proceduresData?.data, 'N')}
									title={brandingData.config_rules?.labels?.n?.toUpperCase()}
								/>
							{getEditorData(postData?.user_type, brandingData?.config_text)?.pa_not_required && (
								<Box
									sx={{
										background: "#dff5ee",
									}}
									className="next-steps-box"
								>
									<div dangerouslySetInnerHTML={createMarkup(getEditorData(postData?.user_type, brandingData?.config_text)?.pa_not_required)}></div>
								</Box>
							)}
							</React.Fragment>
						)}
						{/* Black Part */}
						{proceduresList(proceduresData?.data, 'U')?.length > 0 && (
							<PACheckResultsProcedure 
								color="#000" 
								submitProcCount={proceduresData?.data?.length}
								proceduresList={proceduresList(proceduresData?.data, 'U')}
								title={brandingData.config_rules?.labels?.u?.toUpperCase()}
							/>
						)}
						{/* Start Over Button */}
						<Box className="noPrint" sx={{ textAlign: "left", mt: 2 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={() => navigate(`/${orgId}/${stringConstants.PA_CHECK_FORM_URL}`)}
							>
								START OVER
							</Button>
						</Box>
					</Grid>
				</Grid>
			</Grid>
    </Box>
  );
};

export default PACheckResults;
